import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/ui/StatusBadge';
import { importsApi, type ImportVersion } from '@/lib/imports';
import type { PartnerFull } from '@/lib/onboarding';
import { Section } from './fields';
import { btnPrimary } from './styles';

/** Шаг 2: прайс уходит в существующий AI-импорт; здесь — список версий партнёра и переход в мастер. */
export default function PriceListStep({ partner }: { partner: PartnerFull }) {
  const { t, i18n } = useTranslation();
  const [versions, setVersions] = useState<ImportVersion[] | null>(null);

  useEffect(() => {
    importsApi.list(partner.id).then(setVersions).catch(() => setVersions([]));
  }, [partner.id]);

  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <Section title={t('onb.plUpload')}>
        <p className="text-[12.5px] leading-5 text-foreground-600">{t('onb.plUploadDesc')}</p>
        <Link to={`/import?partner=${partner.id}`} className={`${btnPrimary} mt-4`}>
          <i className="ri-upload-cloud-2-line text-[14px]" /> {t('onb.plOpenImport')}
        </Link>
      </Section>
      <div className="xl:col-span-2">
        <Section title={t('onb.plVersions')}>
          {versions === null ? null : versions.length === 0 ? (
            <p className="text-[12.5px] text-foreground-500">{t('onb.plNoVersions')}</p>
          ) : (
            <ul className="divide-y divide-background-200">
              {versions.map((v) => (
                <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-[12.5px]">
                  <div className="flex items-center gap-3">
                    <span className="tnum font-semibold text-foreground-900">{t('onb.plVersion', { n: v.version })}</span>
                    <StatusBadge tone={v.status === 'Published' ? 'primary' : v.status === 'Draft' ? 'accent' : 'neutral'}>{t(`imp.status.${v.status}`, v.status)}</StatusBadge>
                    <span className="text-foreground-500">{v.fileName}</span>
                    <span className="tnum text-foreground-500">{t('onb.plItems', { count: v.itemsCount ?? 0 })}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-foreground-400">{fmt(v.publishedAt || v.finishedAt || v.createdAt)}</span>
                    <Link to={`/import?v=${v.id}`} className="font-medium text-primary-700">
                      {t('onb.plOpen')}
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}
